import { canRenderLink } from '@components/dashboard/superuser/Sidebar';
import { useAppSelector } from '@hooks/useRedux';
import { queryKeys } from '@lib/queryKeys';
import { FULL_ACCESS_USER_ROLES } from '@shared/constants';
import { UserRole } from '@shared/types';
import {
  selectAccessControlCodes,
  selectCurrentUser,
  selectHasFiscalIntegration,
} from '@store/auth/authSelectors';
import { selectEffectiveOutlet, selectEffectiveOutletId } from '@store/outlets';
import {
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  psGetAll,
  usePowerSyncChange,
} from '@/renderer/lib/powersync/ipcBridge';
import { fetchSecuritySnapshot } from '@/renderer/services/accessControlService';
import { fetchCompanyBilling } from '@/renderer/services/billingService';
import { fetchDevices } from '@/renderer/services/deviceService';
import { fetchEbmConfig } from '@/renderer/services/ebmConfigService';
import { fetchEmailConfigurations } from '@/renderer/services/emailConfigService';
import { fetchMenuItemsMissingFiscalSetup } from '@/renderer/services/menuServicePaginated';
import { getUserLimits } from '@/renderer/services/outletService';
import {
  fetchEbmSubmissionCounts,
  fetchStaffSnapshot,
} from '@/renderer/services/settingsDashboardService';
import {
  DirectoryGroupConfig,
  DirectorySource,
  SETTINGS_DIRECTORY,
} from './settingsDirectoryConfig';
import {
  RowSummary,
  summarizeDevices,
  summarizeEbmDevice,
  summarizeEmailRecipients,
  summarizeFiscalItems,
  summarizeOutletProfile,
  summarizePrinters,
  summarizeRoles,
  summarizeSaleSubmissions,
  summarizeSections,
  summarizeStaff,
  summarizeSubscription,
} from './summaries';

export interface DirectoryRowState {
  summary: RowSummary | null;
  isLoading: boolean;
  isError: boolean;
  /** Outlet-scoped row while "All outlets" is selected. */
  needsOutlet: boolean;
  retry: () => void;
}

const unwrap = <T>(response: {
  success: boolean;
  data?: T;
  error?: string;
}): T => {
  if (!response.success) throw new Error(response.error ?? 'Request failed');
  return response.data as T;
};

const LOCAL_SETTINGS_TABLES = [
  'outlet_sections',
  'outlet_tables',
  'outlet_printers',
  'section_printer_mappings',
  'departments',
];

/**
 * Resolves which Settings Dashboard rows the current user may see and loads
 * each row's summary independently, so one failing source never blanks
 * the rest of the page. Synced tables (sections, printers) read local
 * SQLite and keep working offline.
 */
export function useSettingsDirectory() {
  const queryClient = useQueryClient();
  const currentUser = useAppSelector(selectCurrentUser);
  const accessControlCodes = useAppSelector(selectAccessControlCodes);
  const hasFiscalIntegration = useAppSelector(selectHasFiscalIntegration);
  const outletId = useAppSelector(selectEffectiveOutletId);
  const outlet = useAppSelector(selectEffectiveOutlet);

  const companyId = currentUser?.company?.id ?? null;
  const packageType = currentUser?.subscription?.package ?? null;
  const userRole = (currentUser?.role ?? null) as UserRole | null;
  const hasUnrestrictedAccess = Boolean(
    userRole && FULL_ACCESS_USER_ROLES.includes(userRole),
  );

  const groups: DirectoryGroupConfig[] = useMemo(
    () =>
      SETTINGS_DIRECTORY.map((group) => ({
        ...group,
        rows: group.rows.filter((row) =>
          canRenderLink(
            { ...row, icon: null },
            userRole,
            accessControlCodes,
            hasUnrestrictedAccess,
            hasFiscalIntegration,
          ),
        ),
      })).filter((group) => group.rows.length > 0),
    [userRole, accessControlCodes, hasUnrestrictedAccess, hasFiscalIntegration],
  );

  const visible = useMemo(
    () => new Set(groups.flatMap((group) => group.rows.map((row) => row.id))),
    [groups],
  );
  const has = (source: DirectorySource) => visible.has(source);
  const key = (source: string) =>
    queryKeys.settingsDashboard.source(source, { companyId, outletId });

  usePowerSyncChange(LOCAL_SETTINGS_TABLES, () => {
    queryClient.invalidateQueries({ queryKey: key('sections') });
    queryClient.invalidateQueries({ queryKey: key('printers') });
  });

  // -------------------------------------------------- local (offline-safe)

  const sectionsQuery = useQuery({
    queryKey: key('sections'),
    enabled: has('sections') && Boolean(outletId),
    staleTime: Infinity,
    queryFn: async () => {
      const [sections, tables] = await Promise.all([
        psGetAll<{ count: number }>(
          `SELECT COUNT(*) AS count FROM outlet_sections
           WHERE outlet_id = ? AND COALESCE(is_hidden, 0) = 0`,
          [outletId],
        ),
        psGetAll<{ count: number }>(
          `SELECT COUNT(*) AS count FROM outlet_tables t
           JOIN outlet_sections s ON s.id = t.section_id
           WHERE s.outlet_id = ? AND COALESCE(s.is_hidden, 0) = 0
             AND COALESCE(t.is_hidden, 0) = 0`,
          [outletId],
        ),
      ]);
      return summarizeSections({
        sections: Number(sections[0]?.count ?? 0),
        tables: Number(tables[0]?.count ?? 0),
      });
    },
  });

  const isKdsEnabled = Boolean(outlet?.is_kds_enabled);
  const printersQuery = useQuery({
    queryKey: [...key('printers'), isKdsEnabled],
    enabled: has('printers') && Boolean(outletId),
    staleTime: Infinity,
    queryFn: async () => {
      const [printers, sections, kotDepartments, mappings] = await Promise.all([
        psGetAll<{ is_active: number | boolean }>(
          'SELECT is_active FROM outlet_printers WHERE outlet_id = ?',
          [outletId],
        ),
        psGetAll<{ id: string }>(
          `SELECT id FROM outlet_sections
           WHERE outlet_id = ? AND COALESCE(is_hidden, 0) = 0`,
          [outletId],
        ),
        // With a kitchen display, KOTs go to screens, not printers.
        isKdsEnabled
          ? Promise.resolve([] as { id: string }[])
          : psGetAll<{ id: string }>(
              `SELECT id FROM departments
               WHERE outlet_id = ? AND kot_enabled = 1 AND status = 'active'`,
              [outletId],
            ),
        psGetAll<{
          section_id: string;
          operation: string;
          department_id: string | null;
        }>(
          `SELECT section_id, operation, department_id
           FROM section_printer_mappings WHERE outlet_id = ?`,
          [outletId],
        ),
      ]);
      return summarizePrinters({
        printers: printers.map((p) => ({ is_active: Boolean(p.is_active) })),
        sections: sections.map((s) => ({ id: String(s.id) })),
        kotDepartments: kotDepartments.map((d) => ({ id: String(d.id) })),
        mappings: mappings.map((m) => ({
          section_id: String(m.section_id),
          operation: m.operation,
          department_id: m.department_id ? String(m.department_id) : null,
        })),
      });
    },
  });

  // -------------------------------------------------- Supabase

  const staffQuery = useQuery({
    queryKey: key('staff'),
    enabled: (has('staff') || has('roles')) && Boolean(companyId),
    queryFn: () => fetchStaffSnapshot(outletId),
  });

  const limitsQuery = useQuery({
    queryKey: [...key('user-limits'), packageType],
    enabled: has('staff') && Boolean(companyId && packageType),
    queryFn: async () =>
      unwrap(await getUserLimits(companyId as number, packageType!)),
  });

  const securityQuery = useQuery({
    queryKey: key('security'),
    enabled: has('roles') && Boolean(companyId),
    queryFn: async () =>
      unwrap(await fetchSecuritySnapshot(companyId as number, outletId)),
  });

  const devicesQuery = useQuery({
    queryKey: key('devices'),
    enabled: has('devices'),
    queryFn: async () => unwrap(await fetchDevices(outletId)),
  });

  const ebmDeviceQuery = useQuery({
    queryKey: key('ebm-device'),
    enabled: has('ebmDevice') && Boolean(outletId),
    queryFn: () => fetchEbmConfig(outletId as string),
  });

  const fiscalItemsQuery = useQuery({
    queryKey: key('fiscal-items'),
    enabled: has('fiscalItems'),
    queryFn: async () =>
      unwrap(
        await fetchMenuItemsMissingFiscalSetup({
          outletId,
          page: 1,
          pageSize: 1,
          search: '',
        }),
      ).pagination.totalItems,
  });

  const submissionsQuery = useQuery({
    queryKey: key('sale-submissions'),
    enabled: has('saleSubmissions'),
    queryFn: () => fetchEbmSubmissionCounts(outletId),
  });

  const emailQuery = useQuery({
    queryKey: key('email'),
    enabled: has('email') && Boolean(outletId),
    queryFn: () => fetchEmailConfigurations(outletId as string),
  });

  const billingQuery = useQuery({
    queryKey: key('billing'),
    enabled: has('subscription') && Boolean(companyId),
    queryFn: async () =>
      unwrap(await fetchCompanyBilling(companyId as number)) ?? null,
  });

  // -------------------------------------------------- row states

  const fromQuery = <T>(
    queries: UseQueryResult<unknown>[],
    derive: () => T | null,
  ): Omit<DirectoryRowState, 'needsOutlet'> => {
    // A disabled query (e.g. no plan to look limits up against) stays
    // pending forever; it contributes nothing rather than an endless skeleton.
    const active = queries.filter(
      (q) => !(q.isPending && q.fetchStatus === 'idle'),
    );
    const isError = active.some((q) => q.isError);
    const isLoading = !isError && active.some((q) => q.isPending);
    return {
      summary: isError || isLoading ? null : (derive() as RowSummary | null),
      isLoading,
      isError,
      retry: () => active.forEach((q) => q.isError && q.refetch()),
    };
  };

  const now = new Date();
  const rows: Record<
    DirectorySource,
    Omit<DirectoryRowState, 'needsOutlet'>
  > = {
    outletProfile: {
      summary: outlet ? summarizeOutletProfile(outlet) : null,
      isLoading: !outlet && Boolean(outletId),
      isError: false,
      retry: () => undefined,
    },
    sections: fromQuery([sectionsQuery], () => sectionsQuery.data ?? null),
    printers: fromQuery([printersQuery], () => printersQuery.data ?? null),
    staff: fromQuery([staffQuery, limitsQuery], () =>
      summarizeStaff({
        outletStaff: staffQuery.data ?? [],
        limits: limitsQuery.data ?? null,
      }),
    ),
    roles: fromQuery([securityQuery, staffQuery], () =>
      summarizeRoles({
        policiesCount: securityQuery.data?.policies.length ?? 0,
        outletRoles: (staffQuery.data ?? [])
          .filter((user) => user.is_active)
          .map((user) => user.role),
        assignedRoles: (securityQuery.data?.assignments ?? []).map(
          (assignment) => assignment.role,
        ),
      }),
    ),
    devices: fromQuery([devicesQuery], () =>
      summarizeDevices(devicesQuery.data ?? []),
    ),
    ebmDevice: fromQuery([ebmDeviceQuery], () =>
      summarizeEbmDevice(ebmDeviceQuery.data ?? null, now),
    ),
    fiscalItems: fromQuery([fiscalItemsQuery], () =>
      summarizeFiscalItems(fiscalItemsQuery.data ?? 0),
    ),
    saleSubmissions: fromQuery([submissionsQuery], () =>
      summarizeSaleSubmissions(
        submissionsQuery.data ?? { failed: 0, pending: 0 },
      ),
    ),
    email: fromQuery([emailQuery], () =>
      summarizeEmailRecipients(emailQuery.data ?? []),
    ),
    subscription: fromQuery([billingQuery], () =>
      summarizeSubscription(billingQuery.data ?? null),
    ),
  };

  const rowStates = {} as Record<DirectorySource, DirectoryRowState>;
  groups.forEach((group) =>
    group.rows.forEach((row) => {
      const needsOutlet = row.scope === 'outlet' && !outletId;
      rowStates[row.id] = needsOutlet
        ? {
            summary: null,
            isLoading: false,
            isError: false,
            needsOutlet,
            retry: () => undefined,
          }
        : { ...rows[row.id], needsOutlet };
    }),
  );

  return { groups, rows: rowStates, outlet, outletId };
}
